import type { OnboardingProgress, OnboardingStep, OnboardingStepId } from "@finances/shared";
import { pluralise } from "../../lib/format.js";

/**
 * The getting-started checklist, as prose and links.
 *
 * The server sends counts; everything a person reads is here, next to the route
 * it sends them to, so changing the wording never means touching a handler.
 *
 * Pure functions over the server's answer, deliberately: the checklist has no
 * state of its own beyond "put away", and anything derived from a count is
 * derived on every render rather than remembered and left to go stale.
 */

interface StepCopy {
  label: string;
  /** Shown only while this is the step being asked for. */
  body: string;
  to: string;
  cta: string;
  /** What the step's count counts, for the note beside a finished one. */
  noun: string | null;
}

/**
 * The order they are asked for, which is also the order they depend on each
 * other in: a statement belongs to an account, and a rule and a budget both
 * need a category to point at.
 */
export const ORDER: OnboardingStepId[] = ["household", "account", "statement", "rule", "budget"];

export const STEPS: Record<OnboardingStepId, StepCopy> = {
  household: {
    label: "Name your household",
    body: "A household owns the accounts, the transactions and the rules.",
    to: "/settings",
    cta: "Rename it",
    noun: null,
  },
  account: {
    label: "Add an account",
    body: "A statement belongs to an account, so this one comes first. Add the current accounts, cards and savings you want to keep an eye on.",
    to: "/accounts",
    cta: "Add an account",
    noun: "account",
  },
  statement: {
    label: "Import a statement",
    body: "Download a CSV from your bank and upload it. You get a preview of what would come in, and what is already there, before anything is saved.",
    to: "/import",
    cta: "Import a statement",
    noun: "statement",
  },
  rule: {
    label: "Set up a rule",
    body: "A rule files transactions for you — anything with TESCO in it goes to Groceries. It runs on every import from then on, and never overwrites a category you set by hand.",
    to: "/rules",
    cta: "Write a rule",
    noun: "rule",
  },
  budget: {
    label: "Set a budget",
    body: "Give a category a monthly figure and the dashboard starts telling you how much of it is left.",
    to: "/budgets",
    cta: "Set a budget",
    noun: "budget",
  },
};

/** In dependency order, whatever order the server happened to answer in. */
export function ordered(progress: OnboardingProgress): OnboardingStep[] {
  return [...progress.steps].sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id));
}

export function outstanding(progress: OnboardingProgress): OnboardingStep[] {
  return ordered(progress).filter((step) => !step.done);
}

/** The one being asked for — the first unfinished step, or none left. */
export function current(progress: OnboardingProgress): OnboardingStep | null {
  return outstanding(progress)[0] ?? null;
}

export function position(progress: OnboardingProgress): { done: number; total: number } {
  return {
    done: progress.steps.filter((step) => step.done).length,
    total: progress.steps.length,
  };
}

export function complete(progress: OnboardingProgress): boolean {
  return progress.steps.length > 0 && outstanding(progress).length === 0;
}

/**
 * A finished checklist stays up until it is dismissed, rather than vanishing
 * the moment the last step ticks — the payoff is the point, and a panel that
 * disappears at the instant of success reads as a bug.
 */
export function visible(progress: OnboardingProgress): boolean {
  return !progress.dismissed && progress.steps.length > 0;
}

/** The note beside a finished step. One of a thing is worth naming, several counting. */
export function detail(step: OnboardingStep): string | null {
  if (!step.done || step.count === 0) return null;
  if (step.count === 1 && step.label) return step.label;

  const { noun } = STEPS[step.id];
  return noun ? pluralise(step.count, noun) : null;
}
